import { ChainId, ERC20Token } from '@donaswap/sdk'
import { bscTestnetTokens, bscTokens, ethereumTokens, goerliTestnetTokens } from '@donaswap/tokens'
import type { CommonPrice } from '../../src/fetchFarmsV3'
import type { FarmSupportedChainId } from '../../src'

export const DONASWAP_BNB_LP_MAINNET = '0xfc1C0387fcEeB92F59059e602Eb7683377C6b443'

export type PriceHelper = {
  chain: string
  list: ERC20Token[]
}

export const CHAIN_ID_TO_CHAIN_NAME = {
  [ChainId.FIRECHAIN]: 'firechain',
  [ChainId.RINIA]: 'rinia',
  [ChainId.FIRECHAIN_ZKEVM]: 'firechainZkevm',
  [ChainId.FIRECHAIN_ZKEVM_GHOSTRIDER]: 'firechainZkevmGhostrider',
  [ChainId.BSC]: 'bsc',
  [ChainId.BSC_TESTNET]: 'bscTestnet',
  [ChainId.ETHEREUM]: 'ethereum',
  [ChainId.GOERLI]: 'goerli',
  [ChainId.SEPOLIA]: 'sepolia',
  [ChainId.HOLESKY]: 'holesky',
  [ChainId.POLYGON]: 'polygon',
  [ChainId.POLYGON_AMOY]: 'polygonAmoy',
  [ChainId.POLYGON_ZKEVM]: 'polygonZkevm',
  [ChainId.POLYGON_ZKEVM_TESTNET]: 'polygonZkevmTestnet',
  [ChainId.CRO]: 'cronos',
  [ChainId.CRO_TESTNET]: 'cronosTestnet',
  [ChainId.AVALANCHE]: 'avalanche',
  [ChainId.AVALANCHE_FUJI]: 'avalancheFuji',
  [ChainId.FANTOM_OPERA]: 'fantomOpera',
  [ChainId.FANTOM_TESTNET]: 'fantomTestnet',
  [ChainId.CELO]: 'celo',
  [ChainId.CELO_ALFAJORES]: 'celoAlfajores',
  [ChainId.OPTIMISM]: 'optimism',
  [ChainId.OPTIMISM_GOERLI]: 'optimismGoerli',
  [ChainId.SHIBARIUM]: 'shibarium',
  [ChainId.SHIBARIUM_TESTNET]: 'shibariumTestnet',
  [ChainId.PULSECHAIN]: 'pulsechain',
  [ChainId.PULSECHAIN_TESTNET]: 'pulsechainTestnet',
  [ChainId.XEN]: 'xen',
  [ChainId.XEN_DEVNET]: 'xenDevnet',
  [ChainId.HARMONY]: 'harmony',
  [ChainId.HARMONY_TESTNET]: 'harmonyTestnet',
  [ChainId.COINEX]: 'coinEx',
  [ChainId.COINEX_TESTNET]: 'coinExTestnet',
  [ChainId.DOGECHAIN]: 'dogechain',
  [ChainId.DOGECHAIN_TESTNET]: 'dogechainTestnet',
  [ChainId.ENGRAM_TESTNET]: 'engramTestnet',
  [ChainId.ETHEREUM_CLASSIC]: 'EthereumClassic',
  [ChainId.ETHEREUM_CLASSIC_TESTNET]: 'EthereumClassicTestnet',
  [ChainId.FUSION]: 'fusion',
  [ChainId.FUSION_TESTNET]: 'fusionTestnet',
  [ChainId.HECO]: 'heco',
  [ChainId.HECO_TESTNET]: 'hecoTestnet',
  [ChainId.KCC]: 'kcc',
  [ChainId.KCC_TESTNET]: 'kccTestnet',
  [ChainId.KLAYTN]: 'klaytn',
  [ChainId.KLAYTN_BAOBAB]: 'klaytnBaobob',
  [ChainId.OKXCHAIN]: 'okxChain',
  [ChainId.OKXCHAIN_TESTNET]: 'okxChainTestnet',
  [ChainId.THETHA]: 'theta',
  [ChainId.THETHA_TESTNET]: 'thetaTestnet',
  [ChainId.ULTRON]: 'ultron',
  [ChainId.ULTRON_TESTNET]: 'ultronTestnet',
  [ChainId.MOONBEAM]: 'moonbeam',
  [ChainId.MOONRIVER]: 'moonriver',
  [ChainId.MOONBASE_ALPHA]: 'moonbaseAlpha',
  [ChainId.AURORA]: 'aurora',
  [ChainId.AURORA_TESTNET]: 'auroraTestnet',
  [ChainId.BOBA]: 'boba',
  [ChainId.BOBA_GOERLI]: 'bobaGoerli',
  [ChainId.GNOSIS]: 'gnosis',
  [ChainId.GNOSIS_CHIADO]: 'gnosisChiado',
  [ChainId.METIS]: 'metis',
  [ChainId.METIS_GOERLI]: 'metisGoerli',
  [ChainId.TELOS]: 'telos',
  [ChainId.TELOS_TESTNET]: 'telosTestnet',
  [ChainId.SYSCOIN]: 'syscoin',
  [ChainId.SYSCOIN_TANENBAUM]: 'syscoinTanenbaum',
  [ChainId.IOTEX]: 'iotex',
  [ChainId.IOTEX_TESTNET]: 'iotexTestnet',
  [ChainId.SHIDEN]: 'shiden',
  [ChainId.ASTAR]: 'astar',
  [ChainId.SHIBUNYA]: 'shibunya',
  [ChainId.FUSE]: 'fuse',
  [ChainId.FUSE_SPARKNET]: 'fuseSparknet',
  [ChainId.VELAS]: 'velas',
  [ChainId.VELAS_TESTNET]: 'velasTestnet',
  [ChainId.REI]: 'rei',
  [ChainId.REI_TESTNET]: 'reiTestnet',
  [ChainId.KEKCHAIN]: 'kekchain',
  [ChainId.KEKCHAIN_TESTNET]: 'kekchainTestnet',
  [ChainId.TOMOCHAIN]: 'tomochain',
  [ChainId.TOMOCHAIN_TESTNET]: 'tomochainTestnet',
  [ChainId.THUNDERCORE]: 'thundercore',
  [ChainId.THUNDERCORE_TESTNET]: 'thundercoreTestnet',
  [ChainId.WANCHAIN]: 'wanchain',
  [ChainId.WANCHAIN_TESTNET]: 'wanchainTestnet',
  [ChainId.RSK]: 'rsk',
  [ChainId.RSK_TESTNET]: 'rskTestnet',
  [ChainId.ELASTOS]: 'elastos',
  [ChainId.ELASTOS_TESTNET]: 'elastosTestnet',
  [ChainId.CONFLUX]: 'conflux',
  [ChainId.CONFLUX_TESTNET]: 'confluxTestnet',
  [ChainId.BRISECHAIN]: 'brisebriseTestnet',
  [ChainId.BRISECHAIN_TESTNET]: 'briseTestnet',
  [ChainId.MUUCHAIN]: 'muuchain',
  [ChainId.MUUCHAIN_TESTNET]: 'muuchainTestnet',
  [ChainId.CANTO]: 'canto',
  [ChainId.CANTO_TESTNET]: 'cantoTestnet',
  [ChainId.OASIS_EMERALD]: 'oasis',
  [ChainId.OASIS_TESTNET]: 'oasisTestnet',
  [ChainId.VISION]: 'vision',
  [ChainId.VISION_TESTNET]: 'visionTestnet',
  [ChainId.STEP]: 'step',
  [ChainId.STEP_TESTNET]: 'stepTestnet',
  [ChainId.METER]: 'meter',
  [ChainId.METER_TESTNET]: 'meterTestnet',
  [ChainId.GODWOKEN]: 'godwoken',
  [ChainId.GODWOKEN_TESTNET]: 'godwokenTestnet',
  [ChainId.CALLISTO]: 'callisto',
  [ChainId.CALLISTO_TESTNET]: 'callistoTestnet',
  [ChainId.EVMOS]: 'evmos',
  [ChainId.EVMOS_TESTNET]: 'evmosTestnet',
  [ChainId.ENERGY_WEB_CHAIN]: 'energywebchain',
  [ChainId.ENERGY_VOLTA]: 'energyVolta',
  [ChainId.BASE]: 'base',
  [ChainId.BASE_GOERLI]: 'baseGoerli',
  [ChainId.KAVA]: 'kava',
  [ChainId.KAVA_TESTNET]: 'kavaTestnet',
  [ChainId.CLOVER]: 'clover',
  [ChainId.CLOVER_TESTNET]: 'cloverTestnet',
  [ChainId.DEFICHAIN]: 'defichain',
  [ChainId.DEFICHAIN_TESTNET]: 'defichainTestnet',
  [ChainId.BRONOS]: 'bronos',
  [ChainId.BRONOS_TESTNET]: 'bronosTestnet',
  [ChainId.FILECOIN]: 'filecoin',
  [ChainId.FILECOIN_CALIBRATION]: 'filecoinCalibration',
  [ChainId.FLARE]: 'flare',
  [ChainId.FLARE_TESTNET]: 'flareTestnet',
  [ChainId.TARAXA]: 'taraxa',
  [ChainId.TARAXA_TESTNET]: 'taraxaTestnet',
  [ChainId.ARBITRUM_ONE]: 'arbitrum',
  [ChainId.ARBITRUM_GOERLI]: 'arbitrumGoerli',
  [ChainId.ZKSYNC]: 'era',
  [ChainId.ZKSYNC_TESTNET]: 'eraTestnet',
  [ChainId.LINEA]: 'linea',
  [ChainId.LINEA_TESTNET]: 'lineaTestnet',
  [ChainId.BTTCHAIN]: 'bttchain',
  [ChainId.BTTCHAIN_TESTNET]: 'bttchainTestnet',
  [ChainId.BOBA_AVAX]: 'bobaAvax',
  [ChainId.BOBA_BNB]: 'bobaBnb',
  [ChainId.CMP]: 'cmp',
  [ChainId.CMP_TESTNET]: 'cmpTestnet',
  [ChainId.CORE]: 'core',
  [ChainId.CORE_TESTNET]: 'coreTestnet',
  [ChainId.CUBECHAIN]: 'cubechain',
  [ChainId.CUBECHAIN_TESTNET]: 'cubechain',
  [ChainId.DARWINIA_CRAB]: 'darwiniaCrab',
  [ChainId.DARWINIA_PANGOLIN]: 'darwiniaPangolin',
  [ChainId.DIODE_PRETNET]: 'diode',
  [ChainId.DIODE_TESTNET]: 'diodeTestnet',
  [ChainId.GOCHAIN]: 'gochain',
  [ChainId.GOCHAIN_TESTNET]: 'gochainTestnet',
  [ChainId.HAQQ]: 'haqq',
  [ChainId.HAQQ_TESTEDGE2]: 'haqqTestnet',
  [ChainId.KARDIACHAIN]: 'kardiachain',
  [ChainId.KARDIACHAIN_TESTNET]: 'kardiachainTestnet',
  [ChainId.METADIUM]: 'metadium',
  [ChainId.METADIUM_TESTNET]: 'metadiumTestnet',
  [ChainId.OP_BNB]: 'opBnb',
  [ChainId.OP_BNB_TESTNET]: 'opBnbTestnet',
  [ChainId.PALM]: 'palm',
  [ChainId.PALM_TESTNET]: 'palmTestnet',
  [ChainId.POLIS]: 'polis',
  [ChainId.POLIS_TESTNET]: 'polisTestnet',
  [ChainId.SHIBACHAIN]: 'shibachain',
  [ChainId.SMART_BCH]: 'smartBch',
  [ChainId.SMART_BCH_TESTNET]: 'smartBchTestnet',
  [ChainId.SONGBIRD_CANARY]: 'songbird',
  [ChainId.THAICHAIN]: 'thaichain',
  [ChainId.UBIQ]: 'ubiq',
  [ChainId.UBIQ_TESTNET]: 'ubiqTestnet',
  [ChainId.VECHAIN]: 'vechain',
  [ChainId.VECHAIN_TESTNET]: 'vechainTestnet',
  [ChainId.XINFIN]: 'xinfin',
  [ChainId.XINFIN_APOTHEM]: 'xinfinTestnet',
  [ChainId.SCROLL]: 'scroll',
  [ChainId.SCROLL_SEPOLIA]: 'scrollTestnet',
  [ChainId.XRPL_DEVNET]: 'xrplDevnet',
  [ChainId.ZETACHAIN]: 'zetachain',
  [ChainId.ZETACHAIN_ATHENS]: 'zetachainAthens',
  [ChainId.ZYX]: 'zyx',
} satisfies Record<FarmSupportedChainId, string>

export const priceHelperTokens = {
  [ChainId.ETHEREUM]: {
    chain: 'ethereum',
    list: [ethereumTokens.weth,
      ethereumTokens.usdc,
      ethereumTokens.usdt],
  },
  [ChainId.BSC]: {
    chain: 'bsc',
    list: [bscTokens.wbnb, bscTokens.usdt, bscTokens.busd, bscTokens.eth],
  },
} satisfies Record<number, PriceHelper>

// for testing purposes
export const DEFAULT_COMMON_PRICE: Record<FarmSupportedChainId, CommonPrice> = {
  [ChainId.FIRECHAIN]: {},
  [ChainId.RINIA]: {},
  [ChainId.FIRECHAIN_ZKEVM]: {},
  [ChainId.FIRECHAIN_ZKEVM_GHOSTRIDER]: {},
  [ChainId.ETHEREUM]: {},
  [ChainId.GOERLI]: {
    [goerliTestnetTokens.mockA.address]: '10',
  },
  [ChainId.SEPOLIA]: {},
  [ChainId.HOLESKY]: {},
  [ChainId.BSC]: {},
  [ChainId.BSC_TESTNET]: {
    [bscTestnetTokens.mockA.address]: '10',
    [bscTestnetTokens.usdt.address]: '1',
    [bscTestnetTokens.busd.address]: '1',
    [bscTestnetTokens.usdc.address]: '1',
  },
  [ChainId.POLYGON]: {},
  [ChainId.POLYGON_AMOY]: {},
  [ChainId.POLYGON_ZKEVM]: {},
  [ChainId.POLYGON_ZKEVM_TESTNET]: {},
  [ChainId.CRO]: {},
  [ChainId.CRO_TESTNET]: {},
  [ChainId.AVALANCHE]: {},
  [ChainId.AVALANCHE_FUJI]: {},
  [ChainId.FANTOM_OPERA]: {},
  [ChainId.FANTOM_TESTNET]: {},
  [ChainId.CELO]: {},
  [ChainId.CELO_ALFAJORES]: {},
  [ChainId.OPTIMISM]: {},
  [ChainId.OPTIMISM_GOERLI]: {},
  [ChainId.ARBITRUM_ONE]: {},
  [ChainId.ARBITRUM_GOERLI]: {},
  [ChainId.SHIBARIUM]: {},
  [ChainId.SHIBARIUM_TESTNET]: {},
  [ChainId.PULSECHAIN]: {},
  [ChainId.PULSECHAIN_TESTNET]: {},
  [ChainId.XEN]: {},
  [ChainId.XEN_DEVNET]: {},
  [ChainId.HARMONY]: {},
  [ChainId.HARMONY_TESTNET]: {},
  [ChainId.COINEX_TESTNET]: {},
  [ChainId.COINEX]: {},
  [ChainId.DOGECHAIN]: {},
  [ChainId.DOGECHAIN_TESTNET]: {},
  [ChainId.ENGRAM_TESTNET]: {},
  [ChainId.ETHEREUM_CLASSIC]: {},
  [ChainId.ETHEREUM_CLASSIC_TESTNET]: {},
  [ChainId.FUSION]: {},
  [ChainId.FUSION_TESTNET]: {},
  [ChainId.HECO]: {},
  [ChainId.HECO_TESTNET]: {},
  [ChainId.KCC]: {},
  [ChainId.KCC_TESTNET]: {},
  [ChainId.KLAYTN]: {},
  [ChainId.KLAYTN_BAOBAB]: {},
  [ChainId.OKXCHAIN]: {},
  [ChainId.OKXCHAIN_TESTNET]: {},
  [ChainId.THETHA]: {},
  [ChainId.THETHA_TESTNET]: {},
  [ChainId.ULTRON]: {},
  [ChainId.ULTRON_TESTNET]: {},
  [ChainId.MOONBEAM]: {},
  [ChainId.MOONRIVER]: {},
  [ChainId.MOONBASE_ALPHA]: {},
  [ChainId.AURORA]: {},
  [ChainId.AURORA_TESTNET]: {},
  [ChainId.BOBA]: {},
  [ChainId.BOBA_GOERLI]: {},
  [ChainId.GNOSIS]: {},
  [ChainId.GNOSIS_CHIADO]: {},
  [ChainId.METIS]: {},
  [ChainId.METIS_GOERLI]: {},
  [ChainId.TELOS]: {},
  [ChainId.TELOS_TESTNET]: {},
  [ChainId.SYSCOIN]: {},
  [ChainId.SYSCOIN_TANENBAUM]: {},
  [ChainId.IOTEX]: {},
  [ChainId.IOTEX_TESTNET]: {},
  [ChainId.SHIDEN]: {},
  [ChainId.ASTAR]: {},
  [ChainId.SHIBUNYA]: {},
  [ChainId.FUSE]: {},
  [ChainId.FUSE_SPARKNET]: {},
  [ChainId.VELAS]: {},
  [ChainId.VELAS_TESTNET]: {},
  [ChainId.REI]: {},
  [ChainId.REI_TESTNET]: {},
  [ChainId.KEKCHAIN]: {},
  [ChainId.KEKCHAIN_TESTNET]: {},
  [ChainId.TOMOCHAIN]: {},
  [ChainId.TOMOCHAIN_TESTNET]: {},
  [ChainId.THUNDERCORE]: {},
  [ChainId.THUNDERCORE_TESTNET]: {},
  [ChainId.WANCHAIN]: {},
  [ChainId.WANCHAIN_TESTNET]: {},
  [ChainId.RSK]: {},
  [ChainId.RSK_TESTNET]: {},
  [ChainId.ELASTOS]: {},
  [ChainId.ELASTOS_TESTNET]: {},
  [ChainId.CONFLUX]: {},
  [ChainId.CONFLUX_TESTNET]: {},
  [ChainId.BRISECHAIN]: {},
  [ChainId.BRISECHAIN_TESTNET]: {},
  [ChainId.MUUCHAIN]: {},
  [ChainId.MUUCHAIN_TESTNET]: {},
  [ChainId.CANTO]: {},
  [ChainId.CANTO_TESTNET]: {},
  [ChainId.OASIS_EMERALD]: {},
  [ChainId.OASIS_TESTNET]: {},
  [ChainId.VISION]: {},
  [ChainId.VISION_TESTNET]: {},
  [ChainId.STEP]: {},
  [ChainId.STEP_TESTNET]: {},
  [ChainId.METER]: {},
  [ChainId.METER_TESTNET]: {},
  [ChainId.GODWOKEN]: {},
  [ChainId.GODWOKEN_TESTNET]: {},
  [ChainId.CALLISTO]: {},
  [ChainId.CALLISTO_TESTNET]: {},
  [ChainId.EVMOS]: {},
  [ChainId.EVMOS_TESTNET]: {},
  [ChainId.ENERGY_WEB_CHAIN]: {},
  [ChainId.ENERGY_VOLTA]: {},
  [ChainId.BASE]: {},
  [ChainId.BASE_GOERLI]: {},
  [ChainId.KAVA]: {},
  [ChainId.KAVA_TESTNET]: {},
  [ChainId.CLOVER]: {},
  [ChainId.CLOVER_TESTNET]: {},
  [ChainId.DEFICHAIN]: {},
  [ChainId.DEFICHAIN_TESTNET]: {},
  [ChainId.BRONOS]: {},
  [ChainId.BRONOS_TESTNET]: {},
  [ChainId.FILECOIN]: {},
  [ChainId.FILECOIN_CALIBRATION]: {},
  [ChainId.FLARE]: {},
  [ChainId.FLARE_TESTNET]: {},
  [ChainId.TARAXA]: {},
  [ChainId.TARAXA_TESTNET]: {},
  [ChainId.ZKSYNC]: {},
  [ChainId.ZKSYNC_TESTNET]: {},
  [ChainId.LINEA]: {},
  [ChainId.LINEA_TESTNET]: {},
  [ChainId.BTTCHAIN]: {},
  [ChainId.BTTCHAIN_TESTNET]: {},
  [ChainId.BOBA_AVAX]: {},
  [ChainId.BOBA_BNB]: {},
  [ChainId.CMP]: {},
  [ChainId.CMP_TESTNET]: {},
  [ChainId.CORE]: {},
  [ChainId.CORE_TESTNET]: {},
  [ChainId.CUBECHAIN]: {},
  [ChainId.CUBECHAIN_TESTNET]: {},
  [ChainId.DARWINIA_CRAB]: {},
  [ChainId.DARWINIA_PANGOLIN]: {},
  [ChainId.DIODE_PRETNET]: {},
  [ChainId.DIODE_TESTNET]: {},
  [ChainId.GOCHAIN]: {},
  [ChainId.GOCHAIN_TESTNET]: {},
  [ChainId.HAQQ]: {},
  [ChainId.HAQQ_TESTEDGE2]: {},
  [ChainId.KARDIACHAIN]: {},
  [ChainId.KARDIACHAIN_TESTNET]: {},
  [ChainId.METADIUM]: {},
  [ChainId.METADIUM_TESTNET]: {},
  [ChainId.OP_BNB]: {},
  [ChainId.OP_BNB_TESTNET]: {},
  [ChainId.PALM]: {},
  [ChainId.PALM_TESTNET]: {},
  [ChainId.POLIS]: {},
  [ChainId.POLIS_TESTNET]: {},
  [ChainId.SHIBACHAIN]: {},
  [ChainId.SMART_BCH]: {},
  [ChainId.SMART_BCH_TESTNET]: {},
  [ChainId.SONGBIRD_CANARY]: {},
  [ChainId.THAICHAIN]: {},
  [ChainId.UBIQ]: {},
  [ChainId.UBIQ_TESTNET]: {},
  [ChainId.VECHAIN]: {},
  [ChainId.VECHAIN_TESTNET]: {},
  [ChainId.XINFIN]: {},
  [ChainId.XINFIN_APOTHEM]: {},
  [ChainId.SCROLL]: {},
  [ChainId.SCROLL_SEPOLIA]: {},
  [ChainId.XRPL_DEVNET]: {},
  [ChainId.ZETACHAIN]: {},
  [ChainId.ZETACHAIN_ATHENS]: {},
  [ChainId.ZYX]: {},
}
